<template>
  <block-item-card
    ref="yieldsEvolution"
    :title="$t('ALM.Section.InvestmentsPortfolio.YieldsEvolution.Title')"
    :tooltip="$t('ALM.Section.InvestmentsPortfolio.YieldsEvolution.InfoTip')"
    class="fill-height d-flex flex-column"
  >
    <div class="d-flex align-center ml-4">
      <div class="d-flex align-center mr-4">
        <div
          class="mr-2"
          style="width: 12px; height: 12px; background: #4CAF50; border-radius: 100%;"
        />
        <span class="text-size-12 synthesis-ui-grey-01--text">Yields</span>
      </div>
      <div class="d-flex align-center mr-4">
        <div
          class="mr-2"
          style="width: 12px; height: 12px; background: #2196F3; border-radius: 100%;"
        />
        <span class="text-size-12 synthesis-ui-grey-01--text">Yields Change</span>
      </div>
    </div>

    <v-lazy
      :options="{threshold: .5}"
      transition="fade-transition"
    >
      <e-chart
        :ref="`echart${chartRefId}`"
        :autoresize="true"
        :option="chartOpts"
        :style="`height: 400px;`"
        class="chart"
      />
    </v-lazy>
  </block-item-card>
</template>

<script>
import BlockItemCard      from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors   from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta                     from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency   from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import baseEChartBarProps from '@/theme/default/components/charts/echarts/mixins/bar/baseEChartBarProps'
import baseEChartBarData  from '@/theme/default/components/charts/echarts/mixins/bar/baseEChartBarData'
import chartsColorMixin   from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import { ScatterChart, BarChart } from 'echarts/charts'

// import { toNumberFormatNotationShort } from '@/lib/utils/helper'

import { use }                 from 'echarts/core'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer }      from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'

use([
  BarChart,
  ScatterChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'ALMYieldsEvolution',
  components: { BlockItemCard },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, baseEChartBarProps, baseEChartBarData, chartsColorMixin, riskAnalysisId],
  props     : {},
  dataStore : {
    appToolbarExtended: 'App.Config.AppToolbarExtended',

    Investments: 'ALM.Investments.Data',

    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      breakpoint: (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6)
    }
  },
  computed: {
    yieldsEvolution () {
      return this.Investments.YieldsEvolution || null
    },

    chartOpts () {
      if (!this.yieldsEvolution) return {}

      const yAxis = this.yieldsEvolution.dates.slice().reverse()
      const yieldsData = this.yieldsEvolution.yields.slice().reverse()
      const yieldsChangeData = this.yieldsEvolution.yieldsChange.slice().reverse()

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          axisPointer    : { type: 'shadow' },
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`
            params.forEach(param => {
              result += `<div style="margin: 4px 0;">
              <span style="display:inline-block;width:12px;height:12px;background:${param.color};margin-right:8px;border-radius:2px;"></span>
              <span>${param.seriesName}: ${param.value}%</span>
            </div>`
            })
            return result
          }
        },
        legend: { show: false },
        grid  : {
          left        : 40,
          top         : 50,
          right       : 40, // give the chip room on the right
          bottom      : 0,
          containLabel: true
        },
        xAxis: { show: false },
        yAxis: {
          type     : 'category',
          data     : yAxis,
          axisLabel: {
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 10
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: false }
        },
        series: [
          // yields
          {
            name     : 'Yields',
            type     : 'bar',
            data     : yieldsData,
            itemStyle: { color: '#4CAF50', borderRadius: [0, 4, 4, 0] },
            label    : { show: true, position: ['102%', '30%'], color: 'black', fontSize: 10, fontWeight: 'bold', formatter: (params) => { return params.value + '%' } },

            barWidth       : '35%',
            showBackground : true,
            backgroundStyle: { color: this.themeColorRGBA('synthesis-ui-grey-06') }
          },
          // yields change
          {
            name     : 'Yields Change',
            type     : 'bar',
            data     : yieldsChangeData,
            label    : { show: true, position: ['102%', '30%'], color: 'black', fontSize: 10, fontWeight: 'bold', formatter: (params) => { return params.value + '%' } },
            itemStyle: { color: '#2196F3', borderRadius: [0, 4, 4, 0] },

            barWidth       : '35%',
            barGap         : '10%',
            showBackground : true,
            backgroundStyle: { color: this.themeColorRGBA('synthesis-ui-grey-06') }
          }
        ]
      }
    }
  },
  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }
  },
  created () {
  },
  beforeMount () {},
  mounted () {
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {

  }
}
</script>

<style scoped>
.chart {
  width: 100%;
}
</style>
